import { z } from "zod";

export const EDITION_STATUS_VALUES = [
  "setup",
  "researching",
  "editing",
  "ready_for_release",
  "live",
  "archived",
] as const;

const isoDateString = z
  .string()
  .nullable()
  .refine(value => !value || /^\d{4}-\d{2}-\d{2}$/.test(value), {
    message: "Enter a valid date",
  });

export const BPA_STATUS_VALUES = [
  "not_started",
  "researching",
  "editing",
  "complete",
  "archived",
] as const;

export const SETTABLE_BPA_STATUS_VALUES = [
  "not_started",
  "researching",
  "editing",
  "complete",
] as const;

/**
 * Narrows a row's stored status to one the form accepts, falling back to `not_started` only for a
 * value outside the set.
 *
 * It lives here rather than in a page so there is ONE of it. There were two: this narrowing, and a
 * `status: "not_started"` hardcoded into the load-more path's fresh-row builder. On kdev every row
 * sits at `not_started`, so the hardcoded one read as correct — and on an edition with mixed statuses
 * every appended row rendered `Not started` and a Save the user never asked for wrote that back.
 */
export function toRowStatus(status: string | null): (typeof BPA_STATUS_VALUES)[number] {
  const known = BPA_STATUS_VALUES.find(value => value === status);
  return known ?? "not_started";
}

export const bpaRowFormSchema = z.object({
  id: z.string(),
  assignedResearcherId: z.string().nullable(),
  assignedEditorId: z.string().nullable(),
  status: z.enum(BPA_STATUS_VALUES),
});

/**
 * The publication window cannot close before it opens. Compared on the object rather than either
 * field because it needs both, and reported against `publicationToDate` so the message lands on a
 * field rather than on the form. ISO `yyyy-mm-dd` strings order correctly as strings, and an equal
 * pair is a valid one-day window.
 */
export const manageFormSchema = z
  .object({
    editionConfigId: z.string().min(1, "Select an edition"),
    status: z.enum(EDITION_STATUS_VALUES),
    leadAssigneeId: z.string().nullable(),
    submissionCloseDate: isoDateString,
    publicationFromDate: isoDateString,
    publicationToDate: isoDateString,
    rows: z.array(bpaRowFormSchema),
    productSlugs: z.array(z.string()),
  })
  .superRefine((values, ctx) => {
    const { publicationFromDate, publicationToDate } = values;
    if (!publicationFromDate || !publicationToDate) return;
    if (publicationToDate >= publicationFromDate) return;

    ctx.addIssue({
      code: "custom",
      path: ["publicationToDate"],
      message: "Publication to cannot be before Publication from",
    });
  });

export type ManageFormValues = z.infer<typeof manageFormSchema>;
export type BpaRowFormValues = z.infer<typeof bpaRowFormSchema>;
